import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import settings from '../../settings.json';
import { AppRoutes } from '@/utils/constants';
import { Entry, NavigationSettings } from '@/types/base';
import { translateDynamicKey } from '@/types/translation';

const navSettings: NavigationSettings = settings.navigation;

const isValidRouteId = (id: string): id is keyof typeof AppRoutes => id in AppRoutes;

const getRoute = (id: string): string | undefined => (isValidRouteId(id) ? AppRoutes[id] : undefined);

const toSegments = (pathname: string): string[] => pathname.split('/').filter(Boolean);

const matchesBasePath = (basePath: Entry['basePath'], pathname: string): boolean => {
  if (!basePath) {
    return false;
  }
  const paths = Array.isArray(basePath) ? basePath : [basePath];
  const segments = toSegments(pathname);
  return paths.some(path => segments.includes(path));
};

const matchesRoute = (id: string, pathname: string): boolean => {
  const route = getRoute(id);
  if (!route) {
    return false;
  }
  const staticPrefix = route.split('/:')[0];
  return pathname === staticPrefix || pathname.startsWith(`${staticPrefix}/`);
};

const entryMatches = (entry: Entry, pathname: string): boolean =>
  matchesBasePath(entry.basePath, pathname) ||
  matchesRoute(entry.id, pathname) ||
  (entry.items?.some(subItem => matchesRoute(subItem.id, pathname)) ?? false);

const resolveLabelKey = (pathname: string): string | undefined => {
  const activeEntry = Object.entries(navSettings.entries).find(([, entry]) => entryMatches(entry, pathname));

  if (!activeEntry) {
    return undefined;
  }

  const [, entry] = activeEntry;
  const activeSubItem = entry.items?.find(subItem => matchesRoute(subItem.id, pathname));

  return activeSubItem?.label ?? entry.label;
};

/**
 * Resolves the human-readable name of the current top-level page from the URL,
 * using the shared navigation config. Returns undefined when the route is not
 * part of the main navigation (e.g. detail pages), so callers can fall back to
 * a generic label.
 */
export const useCurrentPageName = (): string | undefined => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const labelKey = resolveLabelKey(pathname);

  return labelKey ? translateDynamicKey(t, labelKey) : undefined;
};
